import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getClients, User } from '../../services/clientService';
import { Favorite as FavoriteIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import { Badge } from '@mui/material';
import { useIsMobile } from '../../hooks/useResponsive';
import './style.css';

const ClientsPage: React.FC = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [favoriteClients, setFavoriteClients] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'favorites'>('favorites');

  // Get token from localStorage
  const getToken = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (isAuthenticated) {
      loadClients();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadClients = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const clients = await getClients(token);
      setFavoriteClients(clients);
    } catch (error) {
      console.error('Error loading clients:', error);
      setFavoriteClients([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US');
  };

  return (
    <div className="clients-page">
      <div className="page-header">
        <h1 className="page-title">
          {language === 'ua' ? 'Мої клієнти' : 'My Clients'}
        </h1>
        <p className="page-subtitle">
          {language === 'ua'
            ? 'Клієнти, які додали мене до улюблених'
            : 'Clients who added me to favorites'
          }
        </p>
      </div>

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            {isMobile ? (
              <Badge
                badgeContent={favoriteClients.length}
                color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: '16px', height: '16px', left: '12px' } }}
              >
                <FavoriteIcon sx={{ fontSize: '1.1rem' }} />
              </Badge>
            ) : (
              <FavoriteIcon sx={{ fontSize: '1.1rem' }} />
            )}
            <span className="tab-text">
              {language === 'ua' ? 'В улюблених' : 'In Favorites'} ({favoriteClients.length})
            </span>
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
          </div>
        ) : favoriteClients.length === 0 ? (
          <div className="empty-state">
            <FavoriteIcon sx={{ fontSize: '3rem', color: '#ccc' }} />
            <h3>
              {language === 'ua' ? 'Немає улюблених клієнтів' : 'No favorite clients yet'}
            </h3>
            <p>
              {language === 'ua'
                ? 'Коли клієнти додадуть вас до улюблених, вони з\'являться тут'
                : 'When clients add you to favorites, they will appear here'
              }
            </p>
          </div>
        ) : (
          <div className="clients-grid">
            {favoriteClients.map((client: User) => (
              <div key={client.id} className="client-card">
                <div className="client-avatar">
                  {client.business_logo ? (
                    <img src={client.business_logo} alt={client.full_name} />
                  ) : (
                    <div className="avatar-placeholder">
                      <i className="bi bi-person"></i>
                    </div>
                  )}
                </div>

                <div className="client-info">
                  <h4>
                    {client.first_name && client.last_name
                      ? `${client.first_name} ${client.last_name}`
                      : client.full_name
                    }
                  </h4>
                  {client.phone && (
                    <p className="client-phone">
                      <PhoneIcon sx={{ fontSize: '1rem', marginRight: '0.5rem' }} />
                      <a
                        href={`tel:${client.phone}`}
                        className="phone-link"
                        title={language === 'ua' ? 'Подзвонити' : 'Call'}
                      >
                        {client.phone}
                      </a>
                    </p>
                  )}
                  {client.email && (
                    <p className="client-email">
                      <EmailIcon sx={{ fontSize: '1rem', marginRight: '0.5rem' }} />
                      <a
                        href={`mailto:${client.email}`}
                        className="email-link"
                        title={language === 'ua' ? 'Написати email' : 'Send email'}
                      >
                        {client.email}
                      </a>
                    </p>
                  )}
                  <p className="client-joined">
                    {language === 'ua' ? 'Приєднався:' : 'Joined:'} {formatDate(client.created_at)}
                  </p>
                  {client.last_login_at && (
                    <p className="client-last-login">
                      {language === 'ua' ? 'Останній вхід:' : 'Last login:'} {formatDate(client.last_login_at)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
